import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import DealSecuritiesPanel from '../components/DealSecuritiesPanel';

/* ── palette ────────────────────────────────────────────────────── */
const C = {
  pageBg: '#0a0f1a', cardBg: '#0d1321', border: '#1e2a3a',
  text: '#e2e8f0', muted: '#6b7a90', accent: '#3b82f6',
  green: '#10b981', red: '#ef4444', orange: '#f59e0b', purple: '#a855f7',
};

const STEPS = ['Deal Details', 'Securities', 'Investor Allocations', 'Review & Submit'];

const INSTRUMENT_TYPES = [
  { value: 'Note', label: 'Convertible Note' },
  { value: 'Preferred', label: 'Convertible Preferred' },
  { value: 'Stock', label: 'Common Stock' },
  { value: 'Warrant', label: 'Warrant' },
  { value: 'ELOC', label: 'ELOC Facility' },
];

const isConvertible = (t) => ['Note', 'Preferred'].includes(t);

/* ── styles ─────────────────────────────────────────────────────── */
const s = {
  page: { padding: '24px 32px', color: C.text, background: C.pageBg, minHeight: '100vh' },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: C.muted, marginTop: 4 },
  stepper: { display: 'flex', gap: 0, marginBottom: 28, marginTop: 20 },
  step: {
    flex: 1, padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 600,
    borderBottom: '3px solid transparent', color: C.muted, transition: 'all 0.2s',
  },
  stepActive: { borderBottomColor: C.accent, color: C.accent },
  stepDone: { borderBottomColor: C.green, color: C.green },
  stepNum: { fontSize: 10, fontWeight: 700, marginBottom: 2 },
  card: {
    background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10,
    padding: 24, marginBottom: 20,
  },
  cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 16, color: C.text },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  formGroupFull: { display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' },
  label: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.muted },
  input: {
    padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
    background: '#0b1120', color: C.text, fontSize: 13, outline: 'none',
  },
  inputError: {
    padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.red}`,
    background: '#0b1120', color: C.text, fontSize: 13, outline: 'none',
    boxShadow: `0 0 0 1px ${C.red}40`,
  },
  select: {
    padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
    background: '#0b1120', color: C.text, fontSize: 13, outline: 'none',
  },
  textarea: {
    padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.border}`,
    background: '#0b1120', color: C.text, fontSize: 13, outline: 'none',
    minHeight: 60, resize: 'vertical', fontFamily: 'inherit',
  },
  btnRow: { display: 'flex', justifyContent: 'space-between', marginTop: 24 },
  btnPrimary: {
    padding: '10px 24px', borderRadius: 6, border: 'none', fontWeight: 700,
    fontSize: 13, cursor: 'pointer', background: C.accent, color: '#fff',
  },
  btnSecondary: {
    padding: '10px 24px', borderRadius: 6, border: `1px solid ${C.border}`,
    fontWeight: 700, fontSize: 13, cursor: 'pointer', background: 'transparent', color: C.muted,
  },
  btnSuccess: {
    padding: '10px 24px', borderRadius: 6, border: 'none', fontWeight: 700,
    fontSize: 13, cursor: 'pointer', background: C.green, color: '#fff',
  },
  btnSmall: {
    padding: '4px 10px', borderRadius: 4, border: `1px solid ${C.border}`,
    background: 'transparent', color: C.accent, fontSize: 11, fontWeight: 600,
    cursor: 'pointer',
  },
  btnSmallRed: {
    padding: '4px 8px', borderRadius: 4, border: 'none',
    background: '#4c0519', color: C.red, fontSize: 11, fontWeight: 600,
    cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  toggle: {
    display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
    border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600,
  },
  toggleBtn: {
    padding: '6px 14px', cursor: 'pointer', border: 'none',
    background: 'transparent', color: C.muted, transition: 'all 0.15s',
  },
  toggleActive: { background: C.accent, color: '#fff' },
  invCard: {
    background: '#0b1120', border: `1px solid ${C.border}`, borderRadius: 8,
    padding: 16, marginBottom: 12,
  },
  invHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  invName: { fontSize: 14, fontWeight: 700, flex: 1 },
  feeRow: {
    display: 'flex', alignItems: 'center', gap: 8, marginTop: 6,
    padding: '6px 10px', background: '#0d1321', borderRadius: 6,
  },
  feeInput: {
    padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.border}`,
    background: '#131d30', color: C.text, fontSize: 12, outline: 'none', width: 70,
    textAlign: 'right',
  },
  feeSelect: {
    padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.border}`,
    background: '#131d30', color: C.text, fontSize: 12, outline: 'none',
  },
  checkbox: { width: 18, height: 18, cursor: 'pointer' },
  reviewRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}22` },
  reviewLabel: { color: C.muted, fontSize: 13 },
  reviewValue: { fontWeight: 600, fontSize: 13 },
  error: { padding: 12, borderRadius: 6, background: '#4c0519', color: C.red, fontSize: 13, marginBottom: 12 },
  fieldError: { fontSize: 11, color: C.red, marginTop: 2 },
  validationBanner: {
    padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16,
    background: '#4c051922', border: `1px solid ${C.red}40`, color: C.red,
    display: 'flex', alignItems: 'center', gap: 8,
  },
};

const fmtUSD = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '$0.00';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ── Comma-formatted money input (formats on blur, raw on focus) ── */
function MoneyField({ style, value, onChange, placeholder, readOnly, ...rest }) {
  const [focused, setFocused] = React.useState(false);
  const fmt = (v) => {
    if (v === '' || v == null) return '';
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return String(v);
    const parts = String(v).split('.');
    const decs = parts.length > 1 ? Math.min(parts[1].length, 4) : 0;
    return n.toLocaleString('en-US', { minimumFractionDigits: decs, maximumFractionDigits: 4 });
  };
  const raw = (v) => v.replace(/[^0-9.\-]/g, '');
  return (
    <input type="text" inputMode="decimal" style={style} readOnly={readOnly}
      value={focused ? (value ?? '') : fmt(value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onChange={e => onChange(raw(e.target.value))} placeholder={placeholder} {...rest} />
  );
}

/* ── Percentage input with % suffix (whole numbers: 5 = 5%) ── */
function PctField({ style, value, onChange, placeholder, width, ...rest }) {
  const w = width || '100%';
  return (
    <div style={{ position: 'relative', width: w, display: 'inline-block' }}>
      <input type="text" inputMode="decimal"
        style={{ ...style, paddingRight: 22, width: '100%', boxSizing: 'border-box' }}
        value={value ?? ''} onChange={e => onChange(e.target.value.replace(/[^0-9.\-]/g, ''))}
        placeholder={placeholder || '0'} {...rest} />
      <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
        color: '#6b7a90', fontSize: 12, pointerEvents: 'none' }}>%</span>
    </div>
  );
}

/* ── component ──────────────────────────────────────────────────── */

function MercuryTxnPicker({ value, txns, getTxnAllocated, getTxnRemaining, onSelect }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  // Close on click outside
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedTxn = value ? txns.find(t => t.mercury_txn_id === value) : null;
  const filtered = txns.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.posted_date || '').includes(q) ||
      (t.counterparty_name || '').toLowerCase().includes(q) ||
      (t.bank_description || '').toLowerCase().includes(q) ||
      (t.kind || '').toLowerCase().includes(q) ||
      (t.mercury_txn_id || '').toLowerCase().includes(q) ||
      String(Math.abs(parseFloat(t.amount || 0))).includes(q);
  });

  const fmtKind = (k) => {
    const m = { externalTransfer: 'WIRE', incomingDomesticWire: 'WIRE IN', outgoingDomesticWire: 'WIRE OUT', incomingInternationalWire: 'INTL WIRE', outgoingInternationalWire: 'INTL WIRE OUT', other: 'OTHER', checkDeposit: 'CHECK', debitCardTransaction: 'DEBIT', achCredit: 'ACH IN', achDebit: 'ACH OUT' };
    return m[k] || (k || '').toUpperCase();
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(!open)} style={{
        padding: '5px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
        background: value ? '#f0fdf4' : '#fff', fontSize: 11, cursor: 'pointer',
        maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: value ? '#15803d' : '#64748b',
      }}>
        {selectedTxn ? (
          <span>{'✅'} {selectedTxn.posted_date} | {(selectedTxn.counterparty_name || '').substring(0, 15)} | ${Math.abs(parseFloat(selectedTxn.amount || 0)).toLocaleString()}</span>
        ) : '\U0001F50D Link Mercury Txn...'}
      </button>

      {value && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(''); }} style={{
          marginLeft: 2, padding: '2px 5px', border: 'none', background: 'transparent',
          color: '#dc2626', fontSize: 10, cursor: 'pointer',
        }}>✕</button>
      )}

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1000,
          width: 520, maxHeight: 360, background: '#fff',
          border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              autoFocus
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by date, counterparty, description, amount, txn ID..."
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ overflow: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No matching transactions</div>
            ) : filtered.slice(0, 500).map(t => {
              const txnAmt = Math.abs(parseFloat(t.amount || 0));
              const allocated = getTxnAllocated(t.mercury_txn_id);
              const isSelected = t.mercury_txn_id === value;
              return (
                <div key={t.mercury_txn_id}
                  onClick={() => { onSelect(t.mercury_txn_id); setOpen(false); setSearch(''); }}
                  style={{
                    padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                    background: isSelected ? '#f0fdf4' : 'transparent',
                    fontSize: 11, lineHeight: 1.4,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = isSelected ? '#f0fdf4' : 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{t.posted_date || 'No date'}</span>
                      <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: t.direction === 'inflow' ? '#dcfce7' : '#fee2e2', color: t.direction === 'inflow' ? '#15803d' : '#dc2626' }}>
                        {t.direction === 'inflow' ? 'IN' : 'OUT'}
                      </span>
                      <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 9, background: '#f1f5f9', color: '#475569' }}>
                        {fmtKind(t.kind)}
                      </span>
                      {t._score > 30 && <span style={{ marginLeft: 4 }}>\u2B50</span>}
                      {t._score > 10 && t._score <= 30 && <span style={{ marginLeft: 4, color: '#f59e0b' }}>\u25CF</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: t.direction === 'inflow' ? '#15803d' : '#dc2626' }}>
                      ${txnAmt.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </div>
                  </div>
                  <div style={{ marginTop: 2, color: '#334155' }}>
                    <span style={{ fontWeight: 500 }}>{t.counterparty_name || 'Unknown'}</span>
                    {t.bank_description && t.bank_description !== t.counterparty_name && (
                      <span style={{ color: '#64748b' }}> \u2014 {t.bank_description}</span>
                    )}
                  </div>
                  <div style={{ marginTop: 2, display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: 10 }}>
                    <span>ID: {(t.mercury_txn_id || '').substring(0, 16)}...</span>
                    <span>{(t.entity_name || '').substring(0, 25)}</span>
                    {allocated > 0 && (
                      <span style={{ color: '#b45309', fontWeight: 600 }}>
                        ${allocated.toLocaleString()} allocated | ${Math.max(0, txnAmt - allocated).toLocaleString()} avail
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected transaction detail summary */}
      {selectedTxn && (
        <div style={{ marginTop: 4, fontSize: 10, color: '#475569', lineHeight: 1.4 }}>
          <div>{selectedTxn.counterparty_name}{selectedTxn.bank_description && selectedTxn.bank_description !== selectedTxn.counterparty_name ? (' \u2014 ' + selectedTxn.bank_description) : ''}</div>
          <div style={{ color: '#94a3b8' }}>{fmtKind(selectedTxn.kind)} | {selectedTxn.entity_name} | ID: {(selectedTxn.mercury_txn_id || '').substring(0, 20)}...</div>
        </div>
      )}
    </div>
  );
}

export default function DealSetupWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editDealId = searchParams.get('edit');
  const isEditMode = !!editDealId;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [createdIds, setCreatedIds] = useState({});
  const [editSecurityId, setEditSecurityId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  /* Validation state — tracks which fields have errors after a Next attempt */
  const [touched, setTouched] = useState(false);

  const [entities, setEntities] = useState([]);
  const [investorRows, setInvestorRows] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [bankFeedTxns, setBankFeedTxns] = useState([]);
  const [txnAllocations, setTxnAllocations] = useState({});
  useEffect(() => { client.get('/securities/deals/', { params: { page_size: 500 } }).then(r => setAllDeals(r.data.results || r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    client.get('/reconciliation/bank-feed/', { params: { page_size: 5000, ordering: '-posted_date' } })
      .then(r => {
        const items = (r.data.results || r.data || [])
          .filter(t => t.amount && parseFloat(t.amount) !== 0)
          .sort((a, b) => (b.posted_date || '').localeCompare(a.posted_date || ''));
        setBankFeedTxns(items);
      }).catch(() => {});
  }, []);

  /* All distributions — used to compute the soft envelope warning for
     Distribution Reinvested capital sources. */
  const [allDistributions, setAllDistributions] = useState([]);
  useEffect(() => {
    client.get('/investors/distributions/', { params: { page_size: 5000 } })
      .then(r => setAllDistributions(r.data.results || r.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    client.get('/investors/capital-sources/', { params: { page_size: 5000 } })
      .then(r => {
        const sources = r.data.results || r.data || [];
        const allocs = {};
        sources.forEach(s => {
          if (s.bank_feed_transaction_id) {
            if (!allocs[s.bank_feed_transaction_id]) allocs[s.bank_feed_transaction_id] = [];
            allocs[s.bank_feed_transaction_id].push({
              id: s.id,
              amount: parseFloat(s.amount || 0),
              deal_name: s.deal_name || '',
              lp_name: s.lp_name || '',
            });
          }
        });
        setTxnAllocations(allocs);
      }).catch(() => {});
  }, []);

  /* Step 1: Deal */
  const [deal, setDeal] = useState({
    deal_name: '', counterparty: '', deal_size: '', entity: '',
    funding_date: '', face_value: '', net_amount: '', oid_pct: '',
    status: 'Active', notes: '', strategy_tag: '', vintage_year: '',
    beneficial_ownership_pct: '', affiliate_status: '',
    warrant_coverage_pct: '', coupon_frequency: '',
  });

  /* Step 2: Primary Security */
  const [secType, setSecType] = useState('Note');
  const [sec, setSec] = useState({
    ticker: '', name: '', issuer: '', principal_amount: '',
    coupon_rate: '', maturity_date: '', issue_date: '',
    interest_mode: 'Cash', day_count: 'ACT/360',
    conversion_price: '', conversion_ratio: '',
    conversion_formula: '', conversion_discount_pct: '',
    conversion_vwap_lookback: '', conversion_floor_price: '', conversion_ceiling_price: '',
    strike_price: '', warrant_shares: '', warrant_expiry: '',
  });
  const [conversionMode, setConversionMode] = useState('fixed');
  const [equity, setEquity] = useState({ ticker: '', name: '', issuer: '' });
  const [createEquity, setCreateEquity] = useState(true);

  /* Tranches (initial funding + add-ons) */
  const emptyTranche = () => ({ id: null, label: '', tranche_date: '', amount: '', face_value: '', oid_amount: '', notes: '' });
  const [tranches, setTranches] = useState([]);
  const [originalTrancheIds, setOriginalTrancheIds] = useState([]);
  const addTranche = () => {
    const idx = tranches.length;
    const defaultLabel = idx === 0 ? 'Initial' : `Add-on #${idx}`;
    setTranches(prev => [...prev, { ...emptyTranche(), label: defaultLabel }]);
  };
  const removeTranche = (idx) => setTranches(prev => prev.filter((_, i) => i !== idx));
  const updateTranche = (idx, field, value) =>
    setTranches(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  const [dealWarrants, setDealWarrants] = useState([]);
  const addWarrant = () => setDealWarrants([...dealWarrants, { series_name: "", strike_price: "", num_shares: "", expiry_date: "", strike_price_method: "", expiry_method: "", is_prefunded: false, exercise_conditions: "", milestone_trigger: "", price_reset_enabled: false, price_reset_floor_pct: "", notes: "" }]);
  const updateWarrant = (i, field, val) => { const w = [...dealWarrants]; w[i] = { ...w[i], [field]: val }; setDealWarrants(w); };
  const removeWarrant = (i) => setDealWarrants(dealWarrants.filter((_, j) => j !== i));

  const [poolRecipients, setPoolRecipients] = useState([{ name: "", type: "Employee", pct: "" }]);
  const [dealTeamMembers, setDealTeamMembers] = useState([]);
  const [showNewMember, setShowNewMember] = useState(false);
  const [newMember, setNewMember] = useState({ member_name: "", role: "Employee", default_split_pct: "" });
  const [creatingMember, setCreatingMember] = useState(false);
  const [newMemberError, setNewMemberError] = useState("");

  const createTeamMember = async () => {
    if (!newMember.member_name.trim()) { setNewMemberError("Name is required"); return; }
    setCreatingMember(true);
    setNewMemberError("");
    try {
      const pct = parseFloat(newMember.default_split_pct);
      const payload = {
        member_name: newMember.member_name.trim(),
        role: newMember.role || "",
        is_active: true,
      };
      if (!isNaN(pct) && pct > 0) payload.default_split_pct = (pct / 100).toFixed(6);
      const res = await client.post('/securities/deal-team/', payload);
      setDealTeamMembers(prev => [...prev, res.data]);
      setNewMember({ member_name: "", role: "Employee", default_split_pct: "" });
      setShowNewMember(false);
    } catch (err) {
      const d = err.response?.data;
      setNewMemberError(typeof d === 'object' ? JSON.stringify(d) : (d || err.message));
    } finally {
      setCreatingMember(false);
    }
  };

  /* Load data */
  useEffect(() => {
    const load = async () => {
      const [entRes, lpRes, dtRes] = await Promise.allSettled([
        client.get('/core/entities/'),
        client.get('/investors/lp-accounts/'),
        client.get('/securities/deal-team/'),
      ]);
      if (dtRes.status === 'fulfilled') {
        setDealTeamMembers((dtRes.value.data.results || dtRes.value.data || []).filter(m => m.is_active !== false));
      } else {
        console.error('Deal team members load failed:', dtRes.reason);
      }
      if (entRes.status === 'fulfilled') {
        setEntities(entRes.value.data.results || entRes.value.data);
      } else {
        console.error('Entities load failed:', entRes.reason);
      }
      if (lpRes.status === 'fulfilled' && !editDealId) {
        const lps = lpRes.value.data.results || lpRes.value.data;
        setInvestorRows(lps.map(lp => ({
          lp_id: lp.id,
          investor_name: lp.investor_name,
          selected: false,
          invested_amount: '',
          ownership_pct: '',
          fee_lines: [],
        })));
      } else if (lpRes.status === 'rejected') {
        console.error('LP accounts load failed:', lpRes.reason);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Load existing deal for edit mode ── */
  useEffect(() => {
    if (!editDealId) return;
    setEditLoading(true);
    const loadDeal = async () => {
      try {
        const dealResp = await client.get(`/securities/deals/${editDealId}/`);
        const d = dealResp.data;
        const st = d.security_terms || {};

        setDeal({
          deal_name: d.deal_name || '', counterparty: d.counterparty || '',
          deal_size: d.deal_size || '', entity: d.entity || '',
          funding_date: d.funding_date || '', face_value: d.face_value || '',
          net_amount: d.net_amount || '', oid_pct: d.oid_pct || '',
          status: d.status || 'Active', notes: d.notes || '',
          strategy_tag: d.strategy_tag || '', vintage_year: d.vintage_year || '',
          beneficial_ownership_pct: d.beneficial_ownership_pct || '',
          affiliate_status: d.affiliate_status || '',
          warrant_coverage_pct: d.warrant_coverage_pct || '',
          coupon_frequency: d.coupon_frequency || '',
        });

        setEditSecurityId(st.id);
        setSecType(st.instrument_type || 'Note');
        setSec({
          ticker: st.ticker || '', name: st.name || '', issuer: st.issuer || '',
          principal_amount: st.principal_amount || '', coupon_rate: st.coupon_rate || '',
          maturity_date: st.maturity_date || '', issue_date: st.issue_date || '',
          interest_mode: st.interest_mode || 'Cash', day_count: st.day_count || 'ACT/360',
          conversion_price: st.conversion_price || '', conversion_ratio: st.conversion_ratio || '',
          conversion_formula: st.conversion_formula || '',
          conversion_discount_pct: st.conversion_discount_pct || '',
          conversion_vwap_lookback: st.conversion_vwap_lookback || '',
          conversion_floor_price: st.conversion_floor_price || '',
          conversion_ceiling_price: st.conversion_ceiling_price || '',
          strike_price: st.strike_price || '', warrant_shares: st.warrant_shares || '',
          warrant_expiry: st.warrant_expiry || '',
        });
        if (st.conversion_formula || st.conversion_discount_pct) setConversionMode('variable');

        /* Investments + fee lines */
        const [invResp, lpResp] = await Promise.all([
          client.get(`/investors/investments/?deal=${editDealId}`),
          client.get('/investors/lp-accounts/'),
        ]);
        const existingInvs = invResp.data.results || invResp.data;
        const lps = lpResp.data.results || lpResp.data;

        // Fetch fees per investment (filtered) to avoid pagination issues
        const feesByInvestment = {};
        await Promise.all(existingInvs.map(async (inv) => {
          const feeResp = await client.get(`/investors/investment-fees/?deal_investment=${inv.id}`);
          feesByInvestment[inv.id] = feeResp.data.results || feeResp.data;
        }));

        // Fetch existing capital sources for this deal
        let existingCapSources = [];
        try {
          const csResp = await client.get('/investors/capital-sources/', { params: { deal_id: editDealId, page_size: 500 } });
          existingCapSources = csResp.data.results || csResp.data || [];
        } catch { /* no capital sources yet */ }

        setInvestorRows(lps.map(lp => {
          const existing = existingInvs.find(inv => inv.lp_account === lp.id);
          if (existing) {
            const fees = feesByInvestment[existing.id] || [];
            // Load existing capital sources for this investment
            const mySources = existingCapSources.filter(cs => cs.deal_investment === existing.id);
            const loadedSources = mySources.length > 0
              ? mySources.map(cs => ({
                  id: cs.id,
                  source_type: cs.source_type || 'fresh',
                  amount: cs.amount || '',
                  source_deal: cs.source_deal || '',
                  funding_date: cs.funding_date || '',
                  fronted_by: cs.fronted_by || '',
                  carry_recipient: cs.carry_recipient || '',
                  bank_feed_transaction_id: cs.bank_feed_transaction_id || '',
                  notes: cs.notes || '',
                }))
              : [{ source_type: 'fresh', amount: '', source_deal: '', funding_date: '', fronted_by: '', carry_recipient: '', bank_feed_transaction_id: '', notes: '' }];
            return {
              lp_id: lp.id, investor_name: lp.investor_name, selected: true,
              capital_sources: loadedSources,
              invested_amount: existing.invested_amount || '',
              ownership_pct: existing.ownership_pct || '',
              fee_lines: fees.map(f => ({ id: f.id, fee_pct: f.fee_pct || '', recipient_type: f.recipient_type || 'Fund' })),
              existing_investment_id: existing.id,
            };
          }
          return { lp_id: lp.id, investor_name: lp.investor_name, selected: false, invested_amount: '', ownership_pct: '', fee_lines: [], capital_sources: [{ source_type: 'fresh', amount: '', source_deal: '', funding_date: '', fronted_by: '', carry_recipient: '', bank_feed_transaction_id: '', notes: '' }] };
        }));

        /* Warrants */
        const warResp = await client.get(`/securities/deal-warrants/?deal=${editDealId}`);
        const ew = warResp.data.results || warResp.data;
        if (ew.length > 0) setDealWarrants(ew.map(w => ({
          id: w.id, series_name: w.series_name || '', strike_price: w.strike_price || '',
          num_shares: w.num_shares || '', expiry_date: w.expiry_date || '',
          strike_price_method: w.strike_price_method || '', expiry_method: w.expiry_method || '',
          is_prefunded: w.is_prefunded || false, exercise_conditions: w.exercise_conditions || '',
          milestone_trigger: w.milestone_trigger || '', price_reset_enabled: w.price_reset_enabled || false,
          price_reset_floor_pct: w.price_reset_floor_pct || '', notes: w.notes || '',
        })));

        /* Tranches */
        try {
          const trResp = await client.get('/securities/deal-tranches/', { params: { deal: editDealId } });
          const trList = trResp.data.results || trResp.data || [];
          trList.sort((a, b) => (a.sort_order - b.sort_order) || ((a.tranche_date || '').localeCompare(b.tranche_date || '')));
          setTranches(trList.map(t => ({
            id: t.id,
            label: t.label || '',
            tranche_date: t.tranche_date || '',
            amount: t.amount || '',
            face_value: t.face_value || '',
            oid_amount: t.oid_amount || '',
            notes: t.notes || '',
          })));
          setOriginalTrancheIds(trList.map(t => t.id));
        } catch { /* no tranches yet */ }

        /* Equity target (conversion target common stock) */
        try {
          const dsResp = await client.get(`/securities/deal-securities/?deal=${editDealId}`);
          const dealSecs = dsResp.data.results || dsResp.data;
          // Identify the conversion target by its series_label, not "any Stock
          // link". Otherwise a common-stock ticker the user added via the
          // Securities panel gets loaded into the equity-target field and then
          // re-created/clobbered on save.
          const stockSec = dealSecs.find(ds => ds.series_label === 'Common Stock (Conversion Target)');
          if (stockSec) {
            setCreateEquity(true);
            setEquity({
              ticker: stockSec.security_ticker || '',
              name: stockSec.security_name || '',
              issuer: stockSec.security_issuer || '',
            });
          } else {
            setCreateEquity(false);
          }
        } catch (e) { setCreateEquity(false); }

        /* Pool recipients */
        try {
          const poolResp = await client.get(`/investors/fee-pool-recipients/?deal=${editDealId}`);
          const ep = poolResp.data.results || poolResp.data;
          if (ep.length > 0) setPoolRecipients(ep.map(pr => ({
            id: pr.id, name: pr.recipient_name || '', type: pr.recipient_type || 'Employee', pct: pr.split_pct || '',
          })));
        } catch (e) { /* pool may not support ?deal= filter */ }

      } catch (err) {
        console.error('Failed to load deal:', err);
        setError('Failed to load deal: ' + (err.response?.data?.detail || err.message));
      } finally {
        setEditLoading(false);
      }
    };
    loadDeal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDealId]);

  /* Auto-calc OID */
  useEffect(() => {
    const face = parseFloat(deal.face_value) || 0;
    const net = parseFloat(deal.net_amount) || 0;
    if (face > 0 && net > 0 && net < face) {
      setDeal(d => ({ ...d, oid_pct: ((face - net) / face * 100).toFixed(4) }));
    } else if (face > 0 && net > 0) {
      setDeal(d => ({ ...d, oid_pct: '0.0000' }));
    }
  }, [deal.face_value, deal.net_amount]);

  const selectedInvestors = investorRows.filter(r => r.selected);
  const totalAllocated = selectedInvestors.reduce((a, r) => a + (parseFloat(r.invested_amount) || 0), 0);
  const totalOwnership = selectedInvestors.reduce((a, r) => a + (parseFloat(r.ownership_pct) || 0), 0);

  /* Field updaters */
  const ud = (f, v) => setDeal(d => ({ ...d, [f]: v }));
  const us = (f, v) => setSec(p => ({ ...p, [f]: v }));
  const ue = (f, v) => setEquity(p => ({ ...p, [f]: v }));
  const ui = (idx, f, v) => setInvestorRows(rows => rows.map((r, i) => i === idx ? { ...r, [f]: v } : r));

  /* Fee line helpers */
  const addFeeLine = (idx) => {
    setInvestorRows(rows => rows.map((r, i) =>
      i === idx ? { ...r, fee_lines: [...r.fee_lines, { fee_pct: '', recipient_type: 'ManagementCompany' }] } : r
    ));
  };
  const updateFeeLine = (rowIdx, feeIdx, field, val) => {
    setInvestorRows(rows => rows.map((r, i) => {
      if (i !== rowIdx) return r;
      const lines = r.fee_lines.map((fl, fi) => fi === feeIdx ? { ...fl, [field]: val } : fl);
      return { ...r, fee_lines: lines };
    }));
  };
  const removeFeeLine = (rowIdx, feeIdx) => {
    setInvestorRows(rows => rows.map((r, i) =>
      i === rowIdx ? { ...r, fee_lines: r.fee_lines.filter((_, fi) => fi !== feeIdx) } : r
    ));
  };

  /* ── Validation ──────────────────────────────────────────────── */
  /* Capital source helpers */
  const addCapSrc = (idx) => setInvestorRows(rows => rows.map((r, i) =>
    i === idx ? { ...r, capital_sources: [...(r.capital_sources || []), { source_type: 'fresh', amount: '', source_deal: '', funding_date: '', fronted_by: '', carry_recipient: '', bank_feed_transaction_id: '', notes: '' }] } : r
  ));
  const removeCapSrc = (idx, csIdx) => setInvestorRows(rows => rows.map((r, i) =>
    i === idx ? { ...r, capital_sources: r.capital_sources.filter((_, j) => j !== csIdx) } : r
  ));
  const updateCapSrc = (idx, csIdx, field, value) => setInvestorRows(rows => rows.map((r, i) =>
    i === idx ? { ...r, capital_sources: r.capital_sources.map((cs, j) => {
      if (j !== csIdx) return cs;
      const next = { ...cs, [field]: value };
      // When source_type changes to one that doesn't support a Mercury link,
      // clear any stale link so it doesn't get persisted.
      if (field === 'source_type' && !['fresh', 'fronted', 'recycled'].includes(value)) {
        next.bank_feed_transaction_id = '';
      }
      // source_deal applies to recycled / distribution / external_recycled / promote_reinvested
      if (field === 'source_type' && !['recycled', 'distribution', 'external_recycled', 'promote_reinvested'].includes(value)) {
        next.source_deal = '';
      }
      // carry_recipient only applies to promote_reinvested
      if (field === 'source_type' && value !== 'promote_reinvested') {
        next.carry_recipient = '';
      }
      return next;
    }) } : r
  ));

  /* Fuzzy-match Mercury transactions to a capital source context */
  const scoreMercuryMatch = (txn, dealName, ticker, fundingDate, investorName, amount) => {
    let score = 0;
    const cp = (txn.counterparty_name || '').toLowerCase();
    const desc = (txn.bank_description || '').toLowerCase();
    const combined = cp + ' ' + desc;
    const tk = (ticker || '').split('_')[0].toLowerCase();
    const dn = (dealName || '').toLowerCase();
    const inv = (investorName || '').toLowerCase();

    // Ticker match in description/counterparty (strongest signal)
    if (tk && tk.length >= 3 && combined.includes(tk)) score += 50;

    // Deal name match
    if (dn && dn.length >= 3) {
      const words = dn.split(/[\s\-_]+/).filter(w => w.length >= 3);
      words.forEach(w => { if (combined.includes(w.toLowerCase())) score += 20; });
    }

    // Investor name match
    if (inv) {
      const invWords = inv.split(/[\s]+/).filter(w => w.length >= 3);
      invWords.forEach(w => { if (combined.includes(w.toLowerCase())) score += 15; });
    }

    // Common fund-related keywords
    if (combined.includes('wire') || combined.includes('investment') || combined.includes('funding') || combined.includes('capital')) score += 5;

    // Date proximity (within 7 days = great, 30 days = okay)
    if (fundingDate && txn.posted_date) {
      const daysDiff = Math.abs((new Date(fundingDate) - new Date(txn.posted_date)) / 86400000);
      if (daysDiff <= 3) score += 30;
      else if (daysDiff <= 7) score += 20;
      else if (daysDiff <= 14) score += 10;
      else if (daysDiff <= 30) score += 5;
    }

    // Amount proximity
    if (amount && txn.amount) {
      const txnAmt = Math.abs(parseFloat(txn.amount));
      const targetAmt = parseFloat(amount);
      if (targetAmt > 0 && txnAmt > 0) {
        const ratio = Math.min(txnAmt, targetAmt) / Math.max(txnAmt, targetAmt);
        if (ratio > 0.95) score += 25;
        else if (ratio > 0.8) score += 15;
        else if (ratio > 0.5) score += 5;
      }
    }

    return score;
  };

  /* Soft envelope check for Distribution Reinvested:
     For a given LP + source_deal, sums the LP's actual distributions from that
     source deal (from allDistributions) vs. their reinvestments from it (across
     all CS rows in current wizard state + previously-saved CS for other deals).
     Returns { distributed, reinvested, warning } or null if not applicable. */
  const getSourceDealEnvelope = (lpId, sourceDealId, currentRowIdx, currentCsIdx) => {
    if (!lpId || !sourceDealId) return null;
    const distributed = allDistributions
      .filter(d => String(d.deal) === String(sourceDealId) && String(d.lp_account) === String(lpId))
      .reduce((s, d) => s + Number(d.net_amount || 0), 0);
    // Sum reinvestments in the current wizard state for this LP from this deal
    let reinvested = 0;
    investorRows.forEach((r, i) => {
      if (r.lp_id !== lpId) return;
      (r.capital_sources || []).forEach((cs, j) => {
        if (i === currentRowIdx && j === currentCsIdx) return; // exclude self when checking
        if (!['recycled', 'distribution'].includes(cs.source_type)) return;
        if (String(cs.source_deal) !== String(sourceDealId)) return;
        reinvested += Number(cs.amount || 0);
      });
    });
    return { distributed, reinvested };
  };

  const getTxnAllocated = (txnId) => {
    // Saved allocations from other deals (exclude current deal's saved sources to avoid double-counting)
    const savedAllocs = txnAllocations[txnId] || [];
    const currentDealSourceIds = new Set();
    investorRows.forEach(r => {
      if (r.selected) {
        (r.capital_sources || []).forEach(cs => { if (cs.id) currentDealSourceIds.add(cs.id); });
      }
    });
    const otherDealsTotal = savedAllocs
      .filter(a => !a.id || !currentDealSourceIds.has(a.id))
      .reduce((sum, a) => sum + a.amount, 0);

    // Current wizard allocations (in-progress, including edits to existing sources)
    let wizardTotal = 0;
    investorRows.forEach(r => {
      if (r.selected) {
        (r.capital_sources || []).forEach(cs => {
          if (cs.bank_feed_transaction_id === txnId && cs.amount) {
            wizardTotal += parseFloat(cs.amount) || 0;
          }
        });
      }
    });

    return otherDealsTotal + wizardTotal;
  };
  const getTxnRemaining = (txn) => {
    const total = Math.abs(parseFloat(txn.amount || 0));
    const allocated = getTxnAllocated(txn.mercury_txn_id);
    return Math.max(0, total - allocated);
  };

  const getScoredTxns = (row) => {
    if (!bankFeedTxns.length) return [];
    const sec = (deal.security_name || '').split('_')[0] || deal.deal_name || '';
    return bankFeedTxns
      .map(t => ({
        ...t,
        _score: scoreMercuryMatch(t, deal.deal_name, sec, deal.funding_date, row.investor_name, row.invested_amount)
      }))
      .sort((a, b) => b._score - a._score);
  };

  const getStepErrors = (stepNum) => {
    const errors = {};
    if (stepNum === 0) {
      if (!deal.deal_name.trim()) errors.deal_name = 'Deal name is required';
      if (!deal.counterparty.trim()) errors.counterparty = 'Counterparty is required';
      if (!deal.deal_size || parseFloat(String(deal.deal_size).replace(/,/g, '')) <= 0) errors.deal_size = 'Deal size is required and must be positive';
    }
    if (stepNum === 1) {
      if (!sec.ticker.trim()) errors.sec_ticker = 'Ticker is required';
      if (!sec.name.trim()) errors.sec_name = 'Security name is required';
    }
    if (stepNum === 2) {
      if (selectedInvestors.length === 0) errors._investors = 'Select at least one investor';
      selectedInvestors.forEach(inv => {
        const allocated = parseFloat(String(inv.invested_amount).replace(/,/g, '')) || 0;
        const ownPct = parseFloat(String(inv.ownership_pct).replace(/,/g, '')) || 0;
        // No cash deployed (e.g. short-and-cover deals where we never wired
        // money): allocate by ownership % alone. Only require a dollar amount
        // when there's no ownership % to go on either.
        if (allocated <= 0) {
          if (ownPct <= 0) {
            errors[`inv_${inv.lp_id}_amount`] = 'Enter an amount or an ownership %';
          }
          return;
        }
        const csSum = (inv.capital_sources || []).reduce(
          (sum, cs) => sum + (parseFloat(cs.amount) || 0), 0
        );
        if (Math.abs(csSum - allocated) > 0.01) {
          const fmt = n => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          errors[`inv_${inv.lp_id}_capsrc`] =
            `Capital sources total ${fmt(csSum)} but this investor is allocated ${fmt(allocated)} (off by ${fmt(Math.abs(allocated - csSum))}).`;
        }
      });
    }
    return errors;
  };

  const canNext = () => Object.keys(getStepErrors(step)).length === 0;

  /* Attempt to advance — show errors if validation fails */
  const tryNext = () => {
    const errors = getStepErrors(step);
    if (Object.keys(errors).length > 0) {
      setTouched(true);
      return;
    }
    setTouched(false);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* Go back — clear validation */
  const goBack = () => {
    if (step > 0) {
      setTouched(false);
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* Current step errors (only shown after a Next attempt) */
  const errs = touched ? getStepErrors(step) : {};
  const hasErrors = Object.keys(errs).length > 0;

  /* Helper: input style with optional error highlight */
  const inputStyle = (fieldKey) => (errs[fieldKey] ? s.inputError : s.input);

  /* Sync tranches against the API. Diffs current state against originalTrancheIds
     so reopened wizards correctly create / update / delete rows. */
  const syncTranches = async (dealId) => {
    if (!dealId) return;
    const currentIds = new Set(tranches.filter(t => t.id).map(t => t.id));
    // Delete tranches that were loaded originally but removed in the UI
    for (const origId of originalTrancheIds) {
      if (!currentIds.has(origId)) {
        try { await client.delete(`/securities/deal-tranches/${origId}/`); } catch { /* ignore */ }
      }
    }
    // Upsert each row in current state
    for (let i = 0; i < tranches.length; i++) {
      const t = tranches[i];
      const hasContent = t.label || t.tranche_date || t.amount || t.face_value || t.oid_amount || t.notes;
      if (!hasContent) continue;
      const payload = {
        deal: parseInt(dealId),
        label: t.label || '',
        tranche_date: t.tranche_date || null,
        amount: t.amount === '' || t.amount == null ? null : parseFloat(t.amount),
        face_value: t.face_value === '' || t.face_value == null ? null : parseFloat(t.face_value),
        oid_amount: t.oid_amount === '' || t.oid_amount == null ? null : parseFloat(t.oid_amount),
        notes: t.notes || '',
        sort_order: i,
      };
      if (t.id) {
        try { await client.put(`/securities/deal-tranches/${t.id}/`, payload); } catch (e) { console.error('Failed to update tranche', t.id, e); }
      } else {
        try { await client.post('/securities/deal-tranches/', payload); } catch (e) { console.error('Failed to create tranche', e); }
      }
    }
  };

  /* Submit */
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEditMode) {
        /* ═══════════ EDIT MODE ═══════════ */

        /* 1. Update security terms */
        const secPayload = { ticker: sec.ticker, name: sec.name, instrument_type: secType, issuer: sec.issuer || deal.counterparty };
        if (secType === 'Note' || secType === 'Preferred') {
          secPayload.principal_amount = sec.principal_amount || null;
          secPayload.coupon_rate = sec.coupon_rate || null;
          secPayload.maturity_date = sec.maturity_date || null;
          secPayload.issue_date = sec.issue_date || deal.funding_date || null;
          secPayload.interest_mode = sec.interest_mode || '';
          secPayload.day_count = sec.day_count || '';
          // Both fixed and variable conversion terms can coexist on a single deal
          secPayload.conversion_price = sec.conversion_price || null;
          secPayload.conversion_ratio = sec.conversion_ratio || null;
          secPayload.conversion_formula = sec.conversion_formula || '';
          secPayload.conversion_discount_pct = sec.conversion_discount_pct || null;
          secPayload.conversion_vwap_lookback = sec.conversion_vwap_lookback || null;
          secPayload.conversion_floor_price = sec.conversion_floor_price || null;
          secPayload.conversion_ceiling_price = sec.conversion_ceiling_price || null;
        }
        if (secType === 'Warrant') {
          secPayload.strike_price = sec.strike_price || null;
          secPayload.warrant_shares = sec.warrant_shares || null;
          secPayload.warrant_expiry = sec.warrant_expiry || null;
        }
        // editSecurityId is set only when the deal already has a primary
        // security. The withdrawn pipeline board left deals with security=null
        // behind, so here we must CREATE the security and link it below —
        // otherwise the ticker the user typed is silently dropped (never saved
        // to the deal, never added to the security master).
        let primarySecId = editSecurityId;
        if (editSecurityId) {
          await client.patch(`/securities/securities/${editSecurityId}/`, secPayload);
        } else if (sec.ticker) {
          const createdSec = await client.post('/securities/securities/', {
            ...secPayload, name: secPayload.name || sec.ticker,
          });
          primarySecId = createdSec.data.id;
        }

        /* 2. Update deal */
        const faceVal = parseFloat(deal.face_value) || null;
        const netAmt = parseFloat(deal.net_amount) || null;
        await client.patch(`/securities/deals/${editDealId}/`, {
          security: primarySecId,
          deal_name: deal.deal_name, counterparty: deal.counterparty,
          entity: deal.entity || null, deal_size: deal.deal_size,
          funding_date: deal.funding_date || null, face_value: faceVal, net_amount: netAmt,
          oid_amount: (faceVal && netAmt) ? parseFloat((faceVal - netAmt).toFixed(4)) : null,
          oid_pct: parseFloat(deal.oid_pct) || null,
          status: deal.status, notes: deal.notes,
          strategy_tag: deal.strategy_tag || null, vintage_year: deal.vintage_year || null,
          beneficial_ownership_pct: deal.beneficial_ownership_pct || null,
          affiliate_status: deal.affiliate_status || null,
          warrant_coverage_pct: deal.warrant_coverage_pct || null,
          coupon_frequency: deal.coupon_frequency || null,
        });

        /* 2b. Sync equity target (conversion target common stock) */
        if (isConvertible(secType)) {
          try {
            const dsResp = await client.get(`/securities/deal-securities/?deal=${editDealId}`);
            const dealSecs = dsResp.data.results || dsResp.data;
            // Only ever touch the conversion-target stock THIS wizard manages,
            // identified by its series_label. Matching on instrument_type ===
            // 'Stock' would clobber any common-stock ticker the user added
            // themselves via the Securities panel.
            const TARGET_LABEL = 'Common Stock (Conversion Target)';
            const existingTarget = dealSecs.find(ds => ds.series_label === TARGET_LABEL);

            if (createEquity && equity.ticker) {
              if (existingTarget) {
                // Update the managed equity-target security
                await client.patch(`/securities/securities/${existingTarget.security}/`, {
                  ticker: equity.ticker, name: equity.name,
                  instrument_type: 'Stock', issuer: equity.issuer || deal.counterparty,
                });
              } else {
                // Create new equity security + link
                const eqResp = await client.post('/securities/securities/', {
                  ticker: equity.ticker, name: equity.name,
                  instrument_type: 'Stock', issuer: equity.issuer || deal.counterparty,
                });
                await client.post('/securities/deal-securities/', {
                  deal: parseInt(editDealId), security: eqResp.data.id,
                  series_label: TARGET_LABEL,
                  is_primary: false, cost_basis_method: 'zero',
                });
              }
            } else if (existingTarget) {
              // User unchecked "create equity target" — remove only that link
              try { await client.delete(`/securities/deal-securities/${existingTarget.id}/`); } catch(e) {}
            }
          } catch (e) { console.warn('Equity target sync:', e); }
        }

        /* 3. Sync investments — delete removed, update existing, create new */
        for (const row of investorRows) {
          if (!row.selected && row.existing_investment_id) {
            try { await client.delete(`/investors/investments/${row.existing_investment_id}/`); } catch(e) { console.warn('Delete inv:', e); }
          }
        }
        for (const inv of selectedInvestors) {
          const totalFeePct = inv.fee_lines.reduce((a, fl) => a + (parseFloat(fl.fee_pct) || 0), 0);
          if (inv.existing_investment_id) {
            await client.patch(`/investors/investments/${inv.existing_investment_id}/`, {
              invested_amount: inv.invested_amount || '0', ownership_pct: inv.ownership_pct || '0',
              incentive_fee_pct: totalFeePct.toFixed(4),
            });
            /* Delete ALL existing fee lines from server, then recreate */
            try {
              const existingFees = await client.get(`/investors/investment-fees/?deal_investment=${inv.existing_investment_id}`);
              for (const f of (existingFees.data.results || existingFees.data)) { try { await client.delete(`/investors/investment-fees/${f.id}/`); } catch(e) {} }
            } catch(e) {}
            for (const fl of inv.fee_lines) {
              if (parseFloat(fl.fee_pct) > 0) await client.post('/investors/investment-fees/', { deal_investment: inv.existing_investment_id, fee_pct: fl.fee_pct, recipient_type: fl.recipient_type });
            }
            /* Sync capital sources — delete old, recreate */
            try {
              const oldCS = await client.get('/investors/capital-sources/', { params: { investment_id: inv.existing_investment_id } });
              for (const cs of (oldCS.data.results || oldCS.data || [])) { try { await client.delete('/investors/capital-sources/' + cs.id + '/'); } catch(e) {} }
            } catch(e) {}
            for (const cs of (inv.capital_sources || [])) {
              if (parseFloat(cs.amount) > 0) {
                const csP = { deal_investment: inv.existing_investment_id, source_type: cs.source_type, amount: cs.amount };
                if (cs.funding_date) csP.funding_date = cs.funding_date;
                if (cs.source_deal) csP.source_deal = parseInt(cs.source_deal);
                if (cs.fronted_by) csP.fronted_by = cs.fronted_by;
                if (cs.carry_recipient) csP.carry_recipient = cs.carry_recipient;
                if (cs.notes) csP.notes = cs.notes;
                if (cs.bank_feed_transaction_id && ['fresh', 'fronted', 'recycled'].includes(cs.source_type)) csP.bank_feed_transaction_id = cs.bank_feed_transaction_id;
                await client.post('/investors/capital-sources/', csP);
              }
            }
          } else {
            const invResp = await client.post('/investors/investments/', {
              lp_account: inv.lp_id, deal: parseInt(editDealId),
              invested_amount: inv.invested_amount || '0', ownership_pct: inv.ownership_pct || '0',
              incentive_fee_pct: totalFeePct.toFixed(4),
              investment_date: deal.funding_date || new Date().toISOString().slice(0, 10),
            });
            for (const fl of inv.fee_lines) {
              if (parseFloat(fl.fee_pct) > 0) await client.post('/investors/investment-fees/', { deal_investment: invResp.data.id, fee_pct: fl.fee_pct, recipient_type: fl.recipient_type });
            }
            /* Capital sources for new investment */
            for (const cs of (inv.capital_sources || [])) {
              if (parseFloat(cs.amount) > 0) {
                const csP = { deal_investment: invResp.data.id, source_type: cs.source_type, amount: cs.amount };
                if (cs.funding_date) csP.funding_date = cs.funding_date;
                if (cs.source_deal) csP.source_deal = parseInt(cs.source_deal);
                if (cs.fronted_by) csP.fronted_by = cs.fronted_by;
                if (cs.notes) csP.notes = cs.notes;
                if (cs.bank_feed_transaction_id && ['fresh', 'fronted', 'recycled'].includes(cs.source_type)) csP.bank_feed_transaction_id = cs.bank_feed_transaction_id;
                await client.post('/investors/capital-sources/', csP);
              }
            }
          }
        }

        /* 4. Sync warrants — delete all & recreate */
        try {
          const curWar = await client.get(`/securities/deal-warrants/?deal=${editDealId}`);
          for (const w of (curWar.data.results || curWar.data)) { try { await client.delete(`/securities/deal-warrants/${w.id}/`); } catch(e) {} }
        } catch(e) {}
        for (const w of dealWarrants) {
          if (w.series_name || w.strike_price || w.num_shares) {
            const wP = { deal: parseInt(editDealId) };
            Object.entries(w).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined && v !== false && k !== 'id') wP[k] = v; });
            await client.post("/securities/deal-warrants/", wP);
          }
        }

        /* 5. Sync pool recipients — delete all & recreate */
        try {
          const curPool = await client.get(`/investors/fee-pool-recipients/?deal=${editDealId}`);
          for (const pr of (curPool.data.results || curPool.data)) { try { await client.delete(`/investors/fee-pool-recipients/${pr.id}/`); } catch(e) {} }
        } catch(e) {}
        const validPoolEdit = poolRecipients.filter(pr => pr.name.trim() && parseFloat(pr.pct) > 0);
        for (const pr of validPoolEdit) {
          await client.post('/investors/fee-pool-recipients/', {
            deal: parseInt(editDealId), recipient_name: pr.name.trim(),
            recipient_type: pr.type || 'Employee', split_pct: pr.pct,
            is_active: true, effective_date: deal.funding_date || new Date().toISOString().slice(0, 10),
          });
        }

        /* 6. Sync tranches */
        await syncTranches(editDealId);

        setCreatedIds({ dealId: editDealId, secId: primarySecId });
        setDone(true);
        return;
      }

      /* ═══════════ CREATE MODE ═══════════ */
      /* 1. Create primary security */
      const secPayload = {
        ticker: sec.ticker, name: sec.name,
        instrument_type: secType,
        issuer: sec.issuer || deal.counterparty,
      };
      if (secType === 'Note' || secType === 'Preferred') {
        secPayload.principal_amount = sec.principal_amount || deal.face_value || null;
        secPayload.coupon_rate = sec.coupon_rate || null;
        secPayload.maturity_date = sec.maturity_date || null;
        secPayload.issue_date = sec.issue_date || deal.funding_date || null;
        secPayload.interest_mode = sec.interest_mode || '';
        secPayload.day_count = sec.day_count || '';
        // Both fixed and variable conversion terms can coexist
        secPayload.conversion_price = sec.conversion_price || null;
        secPayload.conversion_ratio = sec.conversion_ratio || null;
        secPayload.conversion_formula = sec.conversion_formula || '';
        secPayload.conversion_discount_pct = sec.conversion_discount_pct || null;
        secPayload.conversion_vwap_lookback = sec.conversion_vwap_lookback || null;
        secPayload.conversion_floor_price = sec.conversion_floor_price || null;
        secPayload.conversion_ceiling_price = sec.conversion_ceiling_price || null;
      }
      if (secType === 'Warrant') {
        secPayload.strike_price = sec.strike_price || null;
        secPayload.warrant_shares = sec.warrant_shares || null;
        secPayload.warrant_expiry = sec.warrant_expiry || null;
      }
      const secResp = await client.post('/securities/securities/', secPayload);
      const secId = secResp.data.id;

      /* 2. Create equity target (convertibles only) */
      let eqId = null;
      if (isConvertible(secType) && createEquity && equity.ticker) {
        const eqResp = await client.post('/securities/securities/', {
          ticker: equity.ticker, name: equity.name,
          instrument_type: 'Stock', issuer: equity.issuer || deal.counterparty,
        });
        eqId = eqResp.data.id;
      }

      /* 3. Create Deal */
      const faceVal = parseFloat(deal.face_value) || null;
      const netAmt = parseFloat(deal.net_amount) || null;
      const dealResp = await client.post('/securities/deals/', {
        deal_name: deal.deal_name, counterparty: deal.counterparty,
        security: secId, entity: deal.entity || null,
        deal_size: deal.deal_size,
        funding_date: deal.funding_date || null,
        face_value: faceVal, net_amount: netAmt,
        oid_amount: (faceVal && netAmt) ? parseFloat((faceVal - netAmt).toFixed(4)) : null,
        oid_pct: parseFloat(deal.oid_pct) || null,
        status: deal.status, notes: deal.notes,
        strategy_tag: deal.strategy_tag || null,
        vintage_year: deal.vintage_year || null,
        warrant_coverage_pct: deal.warrant_coverage_pct || null,
        coupon_frequency: deal.coupon_frequency || null,
      });
      const dealId = dealResp.data.id;

      /* 3b. Link equity target to deal via DealSecurity */
      if (eqId) {
        await client.post('/securities/deal-securities/', {
          deal: dealId, security: eqId, series_label: 'Common Stock (Conversion Target)',
          is_primary: false, cost_basis_method: 'zero',
        });
      }

      /* 4. Create DealInvestments + fee lines */
      const invIds = [];
      for (const inv of selectedInvestors) {
        const totalFeePct = inv.fee_lines.reduce((a, fl) => a + (parseFloat(fl.fee_pct) || 0), 0);
        const invResp = await client.post('/investors/investments/', {
          lp_account: inv.lp_id, deal: dealId,
          invested_amount: inv.invested_amount || '0',
          ownership_pct: inv.ownership_pct || '0',
          incentive_fee_pct: totalFeePct.toFixed(4),
          investment_date: deal.funding_date || new Date().toISOString().slice(0, 10),
        });
        invIds.push(invResp.data.id);

        for (const fl of inv.fee_lines) {
          if (parseFloat(fl.fee_pct) > 0) {
            await client.post('/investors/investment-fees/', {
              deal_investment: invResp.data.id,
              fee_pct: fl.fee_pct,
              recipient_type: fl.recipient_type,
            });
          }
        }
        /* Capital sources */
        for (const cs of (inv.capital_sources || [])) {
          if (parseFloat(cs.amount) > 0) {
            const csPayload = {
              deal_investment: invResp.data.id,
              source_type: cs.source_type,
              amount: cs.amount,
            };
            if (cs.funding_date) csPayload.funding_date = cs.funding_date;
            if (cs.source_deal) csPayload.source_deal = parseInt(cs.source_deal);
            if (cs.fronted_by) csPayload.fronted_by = cs.fronted_by;
            if (cs.carry_recipient) csPayload.carry_recipient = cs.carry_recipient;
            if (cs.notes) csPayload.notes = cs.notes;
            if (cs.bank_feed_transaction_id && ['fresh', 'fronted', 'recycled'].includes(cs.source_type)) csPayload.bank_feed_transaction_id = cs.bank_feed_transaction_id;
            await client.post('/investors/capital-sources/', csPayload);
          }
        }
      }

      /* 5. Create Fee Pool Recipients (deal team split) */
      const validPool = poolRecipients.filter(pr => pr.name.trim() && parseFloat(pr.pct) > 0);
      for (const pr of validPool) {
        await client.post('/investors/fee-pool-recipients/', {
          deal: dealId,
          recipient_name: pr.name.trim(),
          recipient_type: pr.type || 'Employee',
          split_pct: pr.pct,
          is_active: true,
          effective_date: deal.funding_date || new Date().toISOString().slice(0, 10),
        });
      }

      /* 6. Create Deal Warrants */
      for (const w of dealWarrants) {
        if (w.series_name || w.strike_price || w.num_shares) {
          const wPayload = { deal: dealId };
          Object.entries(w).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined && v !== false) wPayload[k] = v; });
          await client.post("/securities/deal-warrants/", wPayload);
        }
      }

      /* 7. Create Tranches */
      await syncTranches(dealId);

      setCreatedIds({ secId, eqId, dealId, invIds, poolCount: validPool.length, warrantCount: dealWarrants.filter(w => w.series_name || w.strike_price).length });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data;
      setError(typeof detail === 'object' ? JSON.stringify(detail, null, 2) : (detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Step 0: Deal Details ── */
  
  const renderPoolConfig = () => (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e0dbd5", padding: 20, marginTop: 20 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#333332", marginBottom: 4 }}>Deal Team Pool Split</h3>
      <p style={{ fontSize: 12, color: "#6b6b6b", marginBottom: 16 }}>
        {"Configure how the Deal Team portion of incentive fees is split among team members."}
      </p>
      {dealTeamMembers.length === 0 && !showNewMember && (
        <p style={{ fontSize: 12, color: "#9b2226", marginBottom: 12 }}>
          {"No deal team members yet. Click \u201c+ New team member\u201d below to create one."}
        </p>
      )}
      {poolRecipients.map((r, idx) => (
        <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 120px 80px 30px", gap: 8, marginBottom: 8 }}>
          <select value={r.name}
            onChange={e => {
              const a = [...poolRecipients]; const m = dealTeamMembers.find(m => m.member_name === e.target.value);
              a[idx] = { ...a[idx], name: e.target.value, type: m?.role === 'Advisor' ? 'Advisor' : m?.role === 'ManagementCompany' ? 'ManagementCompany' : a[idx].type,
                pct: (!a[idx].pct && m?.default_split_pct) ? String(parseFloat(m.default_split_pct) * 100) : a[idx].pct };
              setPoolRecipients(a);
            }}
            style={inputStyle()}>
            <option value="">— Select team member —</option>
            {dealTeamMembers.map(m => <option key={m.id} value={m.member_name}>{m.member_name}{m.role ? ` (${m.role})` : ''}</option>)}
          </select>
          <select value={r.type}
            onChange={e => { const a = [...poolRecipients]; a[idx] = { ...a[idx], type: e.target.value }; setPoolRecipients(a); }}
            style={inputStyle()}>
            <option value="Employee">Employee</option>
            <option value="ManagementCompany">Mgmt Co</option>
            <option value="Advisor">Advisor</option>
            <option value="Other">Other</option>
          </select>
          <input placeholder="%" value={r.pct} type="text" inputMode="decimal"
            onChange={e => { const a = [...poolRecipients]; a[idx] = { ...a[idx], pct: e.target.value }; setPoolRecipients(a); }}
            style={{ ...inputStyle(), textAlign: "right" }} />
          <button type="button" onClick={() => setPoolRecipients(prev => prev.filter((_, i) => i !== idx))}
            disabled={poolRecipients.length <= 1}
            style={{ border: "none", background: "none", color: poolRecipients.length <= 1 ? "#ccc" : "#9b2226",
                     cursor: poolRecipients.length <= 1 ? "default" : "pointer", fontSize: 16 }}>
            {"\u00d7"}
          </button>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setPoolRecipients(prev => [...prev, { name: "", type: "Employee", pct: "" }])}
            style={{ fontSize: 12, padding: "4px 12px", borderRadius: 4, border: "1px solid #345479",
                     color: "#345479", background: "transparent", cursor: "pointer" }}>
            + Add Row
          </button>
          <button type="button" onClick={() => { setShowNewMember(v => !v); setNewMemberError(""); }}
            style={{ fontSize: 12, padding: "4px 12px", borderRadius: 4, border: "1px solid #2d6a4f",
                     color: "#2d6a4f", background: "transparent", cursor: "pointer" }}>
            {showNewMember ? "Cancel" : "+ New team member"}
          </button>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600,
          color: poolRecipients.reduce((s, r) => s + Number(r.pct || 0), 0) === 100 ? "#2d6a4f" : "#9b2226" }}>
          {"Total: " + poolRecipients.reduce((s, r) => s + Number(r.pct || 0), 0).toFixed(1) + "%"}
          {poolRecipients.reduce((s, r) => s + Number(r.pct || 0), 0) !== 100 ? " (must equal 100%)" : ""}
        </span>
      </div>
      {showNewMember && (
        <div style={{ marginTop: 12, padding: 12, background: "#f7f5f1", borderRadius: 6, border: "1px solid #e0dbd5" }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#333332" }}>Create new deal team member</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 110px auto", gap: 8, alignItems: "center" }}>
            <input placeholder="Full name (e.g. Jeremy Weech)" value={newMember.member_name}
              onChange={e => setNewMember(v => ({ ...v, member_name: e.target.value }))}
              style={inputStyle()} />
            <select value={newMember.role}
              onChange={e => setNewMember(v => ({ ...v, role: e.target.value }))}
              style={inputStyle()}>
              <option value="Employee">Employee</option>
              <option value="ManagementCompany">Mgmt Co</option>
              <option value="Advisor">Advisor</option>
              <option value="Other">Other</option>
            </select>
            <input placeholder="Default %" value={newMember.default_split_pct} type="text" inputMode="decimal"
              onChange={e => setNewMember(v => ({ ...v, default_split_pct: e.target.value }))}
              style={{ ...inputStyle(), textAlign: "right" }} />
            <button type="button" onClick={createTeamMember} disabled={creatingMember || !newMember.member_name.trim()}
              style={{ fontSize: 12, padding: "6px 14px", borderRadius: 4, border: "none",
                       background: creatingMember || !newMember.member_name.trim() ? "#9ca3af" : "#2d6a4f",
                       color: "#fff", cursor: creatingMember || !newMember.member_name.trim() ? "default" : "pointer" }}>
              {creatingMember ? "Saving..." : "Save"}
            </button>
          </div>
          {newMemberError && <div style={{ fontSize: 11, color: "#9b2226", marginTop: 6 }}>{newMemberError}</div>}
        </div>
      )}
    </div>
  );

const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 3 };
  const inp = { width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box" };

  const renderStep0 = () => (
    <div style={s.card}>
      <div style={s.cardTitle}>Deal Details</div>
      {hasErrors && (
        <div style={s.validationBanner}>
          ⚠ Please fill in all required fields highlighted below.
        </div>
      )}
      <div style={s.formGrid}>
        <div style={s.formGroup}>
          <label style={s.label}>Deal Name *</label>
          <input style={inputStyle('deal_name')} value={deal.deal_name} onChange={e => ud('deal_name', e.target.value)} placeholder="e.g. Acme Corp Convertible Note" />
          {errs.deal_name && <div style={s.fieldError}>{errs.deal_name}</div>}
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Counterparty *</label>
          <input style={inputStyle('counterparty')} value={deal.counterparty} onChange={e => ud('counterparty', e.target.value)} placeholder="e.g. Acme Corp" />
          {errs.counterparty && <div style={s.fieldError}>{errs.counterparty}</div>}
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Deal Size ($) *</label>
          <MoneyField style={inputStyle('deal_size')} value={deal.deal_size} onChange={v => ud('deal_size', v)} placeholder="10,000,000" />
          {errs.deal_size && <div style={s.fieldError}>{errs.deal_size}</div>}
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Fund Entity</label>
          <select style={s.select} value={deal.entity} onChange={e => ud('entity', e.target.value)}>
            <option value="">— Select —</option>
            {entities.map(ent => <option key={ent.id} value={ent.id}>{ent.name}</option>)}
          </select>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Funding Date</label>
          <input style={s.input} type="date" value={deal.funding_date} onChange={e => ud('funding_date', e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Status</label>
          <select style={s.select} value={deal.status} onChange={e => ud('status', e.target.value)}>
            <option value="Active">Active</option>
            <option value="Closed">Closed</option>
          </select>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Face Value ($)</label>
          <MoneyField style={s.input} value={deal.face_value} onChange={v => ud('face_value', v)} placeholder="0" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Net Amount ($) (what we actually pay)</label>
          <MoneyField style={s.input} value={deal.net_amount} onChange={v => ud('net_amount', v)} placeholder="0" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>OID % (auto-calculated)</label>
          <input style={{ ...s.input, background: '#131d30' }} value={deal.oid_pct ? deal.oid_pct + '%' : '—'} readOnly />
        </div>
        <div style={s.formGroup} />
        <div style={s.formGroupFull}>
          <label style={s.label}>Notes</label>
          <textarea style={s.textarea} value={deal.notes} onChange={e => ud('notes', e.target.value)} />
        </div>
      </div>

      {/* Tranches & Add-ons */}
      <div style={{ marginTop: 20, borderTop: '2px solid #3a4a5e', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>Tranches & Add-ons ({tranches.length})</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              Record each funding event — initial close plus any later add-ons. Optional; if omitted, the deal is treated as a single funding.
            </div>
          </div>
          <button onClick={addTranche} type='button' style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #2563eb', background: 'transparent', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Tranche</button>
        </div>
        {tranches.length > 0 && (() => {
          const totalAmt = tranches.reduce((s, t) => s + (Number(t.amount) || 0), 0);
          const totalFace = tranches.reduce((s, t) => s + (Number(t.face_value) || 0), 0);
          return (
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
              Total funded: <strong style={{ color: '#e2e8f0' }}>${totalAmt.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong>
              {totalFace > 0 && <> &nbsp;|&nbsp; Total face: <strong style={{ color: '#e2e8f0' }}>${totalFace.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong></>}
            </div>
          );
        })()}
        {tranches.map((tr, i) => (
          <div key={i} style={{ padding: 12, marginBottom: 10, background: 'rgba(37,99,235,0.08)', borderRadius: 8, border: '1px solid rgba(37,99,235,0.2)', position: 'relative' }}>
            <button onClick={() => removeTranche(i)} type='button' style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ef4444', fontSize: 16, cursor: 'pointer', fontWeight: 700 }}>&times;</button>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 8 }}>Tranche {i + 1}</div>
            <div style={s.formGrid}>
              <div style={s.formGroup}>
                <label style={s.label}>Label</label>
                <input style={s.input} value={tr.label} placeholder='Initial / Add-on #1'
                  onChange={e => updateTranche(i, 'label', e.target.value)} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Date</label>
                <input style={s.input} type='date' value={tr.tranche_date}
                  onChange={e => updateTranche(i, 'tranche_date', e.target.value)} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Amount ($)</label>
                <MoneyField style={s.input} value={tr.amount}
                  onChange={v => updateTranche(i, 'amount', v)} placeholder='0.00' />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Face Value ($)</label>
                <MoneyField style={s.input} value={tr.face_value}
                  onChange={v => updateTranche(i, 'face_value', v)} placeholder='0.00' />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>OID ($)</label>
                <MoneyField style={s.input} value={tr.oid_amount}
                  onChange={v => updateTranche(i, 'oid_amount', v)} placeholder='0.00' />
              </div>
              <div style={s.formGroupFull}>
                <label style={s.label}>Notes</label>
                <input style={s.input} value={tr.notes}
                  onChange={e => updateTranche(i, 'notes', e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        {tranches.length === 0 && <div style={{ color: '#64748b', fontSize: 12, fontStyle: 'italic' }}>No tranches recorded. Click + Add Tranche to track each funding event.</div>}
      </div>

      {/* Warrants */}
      <div style={{ marginTop: 20, borderTop: '2px solid #3a4a5e', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>Warrants ({dealWarrants.length})</div>
          <button onClick={addWarrant} type='button' style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #7c3aed', background: 'transparent', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Warrant</button>
        </div>
        {dealWarrants.map((w, i) => (
          <div key={i} style={{ padding: 12, marginBottom: 10, background: 'rgba(124,58,237,0.08)', borderRadius: 8, border: '1px solid rgba(124,58,237,0.2)', position: 'relative' }}>
            <button onClick={() => removeWarrant(i)} type='button' style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ef4444', fontSize: 16, cursor: 'pointer', fontWeight: 700 }}>&times;</button>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>Warrant {i + 1}</div>
            <div style={s.formGrid}>
              <div style={s.formGroup}><label style={s.label}>Series Name</label><input style={s.input} value={w.series_name} onChange={e => updateWarrant(i, 'series_name', e.target.value)} placeholder='e.g. Series B Warrants' /></div>
              <div style={s.formGroup}><label style={s.label}>Strike Price ($)</label><MoneyField style={s.input} value={w.strike_price} onChange={v => updateWarrant(i, 'strike_price', v)} placeholder='0.00' /></div>
              <div style={s.formGroup}><label style={s.label}>Number of Shares</label><MoneyField style={s.input} value={w.num_shares} onChange={v => updateWarrant(i, 'num_shares', v)} placeholder='0' /></div>
              <div style={s.formGroup}><label style={s.label}>Expiry Date</label><input style={s.input} type='date' value={w.expiry_date} onChange={e => updateWarrant(i, 'expiry_date', e.target.value)} /></div>
              <div style={s.formGroup}><label style={s.label}>Strike Price Method</label><input style={s.input} value={w.strike_price_method} onChange={e => updateWarrant(i, 'strike_price_method', e.target.value)} placeholder='e.g. 90% of closing price' /></div>
              <div style={s.formGroup}><label style={s.label}>Expiry Method</label><input style={s.input} value={w.expiry_method} onChange={e => updateWarrant(i, 'expiry_method', e.target.value)} placeholder='e.g. 18mo after approval' /></div>
              <div style={s.formGroupFull}><label style={s.label}>Exercise Conditions</label><textarea style={s.textarea} value={w.exercise_conditions} onChange={e => updateWarrant(i, 'exercise_conditions', e.target.value)} placeholder='e.g. Requires stockholder approval' /></div>
              <div style={s.formGroup}><label style={s.label}>Milestone Trigger</label><input style={s.input} value={w.milestone_trigger} onChange={e => updateWarrant(i, 'milestone_trigger', e.target.value)} placeholder='e.g. Clinical data announcement' /></div>
              <div style={s.formGroup}>
                <label style={s.label}>Options</label>
                <div style={{ display: 'flex', gap: 16, paddingTop: 6 }}>
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8' }}><input type='checkbox' checked={w.is_prefunded} onChange={e => updateWarrant(i, 'is_prefunded', e.target.checked)} /> Pre-Funded</label>
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8' }}><input type='checkbox' checked={w.price_reset_enabled} onChange={e => updateWarrant(i, 'price_reset_enabled', e.target.checked)} /> Price Reset</label>
                </div>
              </div>
            </div>
            {w.price_reset_enabled && (<div style={{ marginTop: 6 }}><label style={s.label}>Reset Floor (% of original)</label><PctField style={{ ...s.input, maxWidth: 200 }} value={w.price_reset_floor_pct} onChange={v => updateWarrant(i, 'price_reset_floor_pct', v)} placeholder='50' /></div>)}
          </div>
        ))}
        {dealWarrants.length === 0 && <div style={{ color: '#64748b', fontSize: 12, fontStyle: 'italic' }}>No warrants attached. Click + Add Warrant to add warrant series.</div>}
      </div>
    </div>
  );

  /* ── Step 1: Securities ── */
  const renderStep1 = () => (
    <>
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={s.cardTitle}>Primary Security</div>
          <div style={s.toggle}>
            {INSTRUMENT_TYPES.map(t => (
              <button key={t.value} style={{ ...s.toggleBtn, ...(secType === t.value ? s.toggleActive : {}) }}
                onClick={() => setSecType(t.value)}>{t.label}</button>
            ))}
          </div>
        </div>
        {hasErrors && (
          <div style={s.validationBanner}>
            ⚠ Please fill in all required fields highlighted below.
          </div>
        )}
        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.label}>Ticker *</label>
            <input style={inputStyle('sec_ticker')} value={sec.ticker} onChange={e => us('ticker', e.target.value)} placeholder="e.g. ACME-NOTE" />
            {errs.sec_ticker && <div style={s.fieldError}>{errs.sec_ticker}</div>}
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Security Name *</label>
            <input style={inputStyle('sec_name')} value={sec.name} onChange={e => us('name', e.target.value)} />
            {errs.sec_name && <div style={s.fieldError}>{errs.sec_name}</div>}
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Issuer</label>
            <input style={s.input} value={sec.issuer} onChange={e => us('issuer', e.target.value)} placeholder={deal.counterparty || 'Auto from deal'} />
          </div>

          {(secType === 'Note' || secType === 'Preferred') && (<>
            <div style={s.formGroup}>
              <label style={s.label}>Principal / Face ($)</label>
              <MoneyField style={s.input} value={sec.principal_amount} onChange={v => us('principal_amount', v)} placeholder={deal.face_value || '0'} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>{secType === 'Preferred' ? 'Dividend Rate (%)' : 'Coupon Rate (%)'}</label>
              <PctField style={s.input} value={sec.coupon_rate} onChange={v => us('coupon_rate', v)} placeholder="8" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Interest / Payment Mode</label>
              <select style={s.select} value={sec.interest_mode} onChange={e => us('interest_mode', e.target.value)}>
                <option value="Cash">Cash Pay</option><option value="PIK">PIK</option><option value="Hybrid">Hybrid</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Day Count</label>
              <select style={s.select} value={sec.day_count} onChange={e => us('day_count', e.target.value)}>
                <option value="ACT/360">ACT/360</option><option value="ACT/365">ACT/365</option><option value="30/360">30/360</option><option value="ACT/ACT">ACT/ACT</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Issue Date</label>
              <input style={s.input} type="date" value={sec.issue_date} onChange={e => us('issue_date', e.target.value)} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Maturity Date</label>
              <input style={s.input} type="date" value={sec.maturity_date} onChange={e => us('maturity_date', e.target.value)} />
            </div>
          </>)}

          {secType === 'Warrant' && (<>
            <div style={s.formGroup}>
              <label style={s.label}>Strike Price ($)</label>
              <MoneyField style={s.input} value={sec.strike_price} onChange={v => us('strike_price', v)} placeholder="0.00" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Warrant Shares</label>
              <MoneyField style={s.input} value={sec.warrant_shares} onChange={v => us('warrant_shares', v)} placeholder="0" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Expiry Date</label>
              <input style={s.input} type="date" value={sec.warrant_expiry} onChange={e => us('warrant_expiry', e.target.value)} />
            </div>
          </>)}
        </div>
      </div>

      {/* Conversion Terms */}
      {isConvertible(secType) && (
        <div style={s.card}>
          <div style={s.cardTitle}>Conversion Terms</div>
          <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: 8, marginBottom: 14, fontSize: 11, color: '#3730a3' }}>
            Fill in all applicable terms — fixed and variable mechanics can coexist on a single deal.
          </div>
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Fixed Conversion Price ($)</label>
              <MoneyField style={s.input} value={sec.conversion_price} onChange={v => us('conversion_price', v)} placeholder="0.00" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Conversion Ratio (shares per unit)</label>
              <MoneyField style={s.input} value={sec.conversion_ratio} onChange={v => us('conversion_ratio', v)} placeholder="0" />
            </div>
            <div style={s.formGroupFull}>
              <label style={s.label}>Variable Conversion Formula</label>
              <input style={s.input} value={sec.conversion_formula} onChange={e => us('conversion_formula', e.target.value)} placeholder="e.g. Lower of Fixed or 90% of 5-day VWAP" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Discount to Market (%)</label>
              <PctField style={s.input} value={sec.conversion_discount_pct} onChange={v => us('conversion_discount_pct', v)} placeholder="10" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>VWAP Lookback (days)</label>
              <input style={s.input} type="text" inputMode="numeric" value={sec.conversion_vwap_lookback} onChange={e => us('conversion_vwap_lookback', e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 5" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Floor Price ($)</label>
              <MoneyField style={s.input} value={sec.conversion_floor_price} onChange={v => us('conversion_floor_price', v)} placeholder="0.00" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Ceiling Price ($)</label>
              <MoneyField style={s.input} value={sec.conversion_ceiling_price} onChange={v => us('conversion_ceiling_price', v)} placeholder="0.00" />
            </div>
          </div>
        </div>
      )}

      {/* Equity Target */}
      {isConvertible(secType) && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <input type="checkbox" style={s.checkbox} checked={createEquity} onChange={e => setCreateEquity(e.target.checked)} />
            <div style={{ ...s.cardTitle, marginBottom: 0 }}>Create Common Stock (Conversion Target)</div>
          </div>
          {createEquity && (
            <div style={s.formGrid}>
              <div style={s.formGroup}>
                <label style={s.label}>Ticker *</label>
                <input style={s.input} value={equity.ticker} onChange={e => ue('ticker', e.target.value)} placeholder="e.g. ACME" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Security Name *</label>
                <input style={s.input} value={equity.name} onChange={e => ue('name', e.target.value)} placeholder="e.g. Acme Corp Common Stock" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Issuer</label>
                <input style={s.input} value={equity.issuer} onChange={e => ue('issuer', e.target.value)} placeholder={deal.counterparty || 'Auto from deal'} />
              </div>
            </div>
          )}
        </div>
      )}
      {isEditMode && editDealId && <DealSecuritiesPanel dealId={parseInt(editDealId)} />}
    </>
  );

  /* ── Step 2: Investor Allocations ── */
  const renderStep2 = () => (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={s.cardTitle}>Investor Allocations & Fee Structure</div>
        <div style={{ fontSize: 12, color: C.muted }}>
          Net Funding: <strong style={{ color: C.accent }}>{fmtUSD(deal.net_amount || deal.deal_size)}</strong>
          {' | '}Allocated: <strong style={{ color: totalAllocated > parseFloat(deal.net_amount || deal.deal_size || 0) ? C.red : C.green }}>{fmtUSD(totalAllocated)}</strong>
          {' | '}Ownership: <strong style={{ color: Math.abs(totalOwnership - 100) < 0.01 ? C.green : C.orange }}>{totalOwnership.toFixed(2)}%</strong>
        </div>
      </div>

      {hasErrors && (() => {
        const capsrcKey = Object.keys(errs).find(k => k.endsWith('_capsrc'));
        return (
          <div style={s.validationBanner}>
            ⚠ {errs._investors
              ? errs._investors
              : capsrcKey
                ? 'Capital sources must sum to each investor’s allocated amount before you can continue.'
                : 'Each selected investor needs an investment amount or an ownership %.'}
          </div>
        );
      })()}

      {investorRows.map((row, idx) => {
        const amountErr = errs[`inv_${row.lp_id}_amount`];
        return (
          <div key={row.lp_id} style={{
            ...s.invCard,
            borderColor: amountErr ? C.red + '80' : row.selected ? C.accent + '60' : C.border,
          }}>
            <div style={s.invHeader}>
              <input type="checkbox" style={s.checkbox} checked={row.selected}
                onChange={e => ui(idx, 'selected', e.target.checked)} />
              <div style={s.invName}>{row.investor_name}</div>
              {row.selected && (<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>Amount ($):</span>
                    <input
                      style={{
                        ...s.feeInput, width: 120,
                        ...(amountErr ? { borderColor: C.red, boxShadow: `0 0 0 1px ${C.red}40` } : {}),
                      }}
                      type="text" inputMode="decimal" value={row.invested_amount}
                      onChange={e => {
                        const cleaned = e.target.value.replace(/[^0-9.\-]/g, '');
                        ui(idx, 'invested_amount', cleaned);
                        const amt = parseFloat(cleaned) || 0;
                        const ds = parseFloat(deal.net_amount || deal.deal_size) || 0;
                        if (ds > 0) ui(idx, 'ownership_pct', (amt / ds * 100).toFixed(4));
                      }} placeholder="0" />
                  </div>
                  {amountErr && <div style={s.fieldError}>{amountErr}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>Own %:</span>
                  <PctField style={{ ...s.feeInput, width: 70 }} width="70px"
                    value={row.ownership_pct} onChange={v => ui(idx, 'ownership_pct', v)} placeholder="0" />
                </div>
              </>)}
            </div>

            {row.selected && (
              <div style={{ marginLeft: 30 }}>
                {row.fee_lines.length === 0 && (
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, fontStyle: 'italic' }}>
                    No incentive fee (0%) — click below to add
                  </div>
                )}
                {row.fee_lines.map((fl, feeIdx) => (
                  <div key={feeIdx} style={s.feeRow}>
                    <PctField style={s.feeInput} value={fl.fee_pct} placeholder="0" width="70px"
                      onChange={v => updateFeeLine(idx, feeIdx, 'fee_pct', v)} />
                    <span style={{ fontSize: 12, color: C.muted }}>to</span>
                    <select style={s.feeSelect} value={fl.recipient_type}
                      onChange={e => updateFeeLine(idx, feeIdx, 'recipient_type', e.target.value)}>
                      <option value="ManagementCompany">Management Company</option>
                      <option value="DealTeam">Deal Team</option>
                    </select>
                    <button style={s.btnSmallRed} onClick={() => removeFeeLine(idx, feeIdx)}>✕</button>
                  </div>
                ))}
                <button style={{ ...s.btnSmall, marginTop: 6 }} onClick={() => addFeeLine(idx)}>+ Add Fee Line</button>

                {/* Capital Sources */}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#345479', marginBottom: 6 }}>Capital Funding Sources</div>
                  {(row.capital_sources || []).map((cs, csIdx) => (
                    <div key={csIdx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                      <select style={{ ...s.feeInput, width: 150 }} value={cs.source_type}
                        onChange={e => updateCapSrc(idx, csIdx, 'source_type', e.target.value)}>
                        <option value="fresh">Fresh $</option>
                        <option value="recycled">Recycled</option>
                        <option value="fronted">Fronted</option>
                        <option value="retained">Retained</option>
                        <option value="distribution">Dist. Reinvested</option>
                        <option value="promote_reinvested">Promote Reinvested</option>
                        <option value="direct_payment">Direct Payment</option>
                        <option value="external_recycled">JV/Pool Recycled</option>
                      </select>
                      <input style={{ ...s.feeInput, width: 100 }} type="text" inputMode="decimal"
                        value={cs.amount} onChange={e => updateCapSrc(idx, csIdx, 'amount', e.target.value.replace(/[^0-9.\-]/g, ''))}
                        placeholder="Amount" />
                      {(cs.source_type === 'recycled' || cs.source_type === 'distribution' || cs.source_type === 'external_recycled' || cs.source_type === 'promote_reinvested') && (
                        <select style={{ ...s.feeInput, width: 130 }} value={cs.source_deal}
                          onChange={e => updateCapSrc(idx, csIdx, 'source_deal', e.target.value)}>
                          <option value="">Source deal...</option>
                          {allDeals.map(d => <option key={d.id} value={d.id}>{d.deal_name}</option>)}
                        </select>
                      )}
                      {cs.source_type === 'promote_reinvested' && (
                        <select style={{ ...s.feeInput, width: 150 }} value={cs.carry_recipient || ''}
                          onChange={e => updateCapSrc(idx, csIdx, 'carry_recipient', e.target.value)}>
                          <option value="">— Carry recipient —</option>
                          {dealTeamMembers.map(m => <option key={m.id} value={m.member_name}>{m.member_name}</option>)}
                        </select>
                      )}
                      {cs.source_type === 'fronted' && (
                        <select style={{ ...s.feeInput, width: 140 }} value={cs.fronted_by}
                          onChange={e => updateCapSrc(idx, csIdx, 'fronted_by', e.target.value)}>
                          <option value="">— Fronted by —</option>
                          {entities.map(ent => <option key={ent.id} value={ent.name}>{ent.name}</option>)}
                        </select>
                      )}
                      {cs.source_type === 'direct_payment' && (
                        <select style={{ ...s.feeInput, width: 150 }} value={cs.fronted_by || ''}
                          onChange={e => updateCapSrc(idx, csIdx, 'fronted_by', e.target.value)}>
                          <option value="">— Who paid —</option>
                          {entities.map(ent => <option key={ent.id} value={ent.name}>{ent.name}</option>)}
                        </select>
                      )}
                      {cs.source_type === 'external_recycled' && (
                        <select style={{ ...s.feeInput, width: 150 }} value={cs.fronted_by || ''}
                          onChange={e => updateCapSrc(idx, csIdx, 'fronted_by', e.target.value)}>
                          <option value="">— Held by (JV/pool) —</option>
                          {entities.map(ent => <option key={ent.id} value={ent.name}>{ent.name}</option>)}
                        </select>
                      )}
                      <input style={{ ...s.feeInput, width: 100 }} type="date" value={cs.funding_date || ''}
                        onChange={e => updateCapSrc(idx, csIdx, 'funding_date', e.target.value)} />
                      {['fresh', 'fronted', 'recycled'].includes(cs.source_type) && (
                      <MercuryTxnPicker
                        value={cs.bank_feed_transaction_id}
                        txns={getScoredTxns(row)}
                        getTxnAllocated={getTxnAllocated}
                        getTxnRemaining={getTxnRemaining}
                        onSelect={(txnId) => {
                          updateCapSrc(idx, csIdx, 'bank_feed_transaction_id', txnId);
                          if (txnId) {
                            const txn = bankFeedTxns.find(t => t.mercury_txn_id === txnId);
                            if (txn && !cs.amount) { const rem = getTxnRemaining(txn); updateCapSrc(idx, csIdx, 'amount', rem > 0 ? rem.toString() : Math.abs(parseFloat(txn.amount)).toString()); }
                            if (txn && !cs.funding_date && txn.posted_date) updateCapSrc(idx, csIdx, 'funding_date', txn.posted_date);
                          }
                        }}
                      />
                      )}
                      {cs.source_type === 'direct_payment' && (
                        <input style={{ ...s.feeInput, width: 200 }} type="text"
                          value={cs.notes || ''} onChange={e => updateCapSrc(idx, csIdx, 'notes', e.target.value)}
                          placeholder="e.g. Wire sent directly to [Co]" />
                      )}
                      {cs.source_type === 'retained' && (
                        <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                          No Mercury link — retained earnings already booked from prior fee/carry inflows.
                        </span>
                      )}
                      {cs.source_type === 'distribution' && (
                        <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                          No Mercury link — synthetic distribution, no real outbound wire.
                        </span>
                      )}
                      {cs.source_type === 'external_recycled' && (
                        <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                          No Mercury link — JV / external pool reallocates its own cash; reclassification posts on Helena's books.
                        </span>
                      )}
                      {cs.source_type === 'promote_reinvested' && (
                        <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                          No Mercury link — recipient's earned carry converted to investment, no cash leg.
                        </span>
                      )}
                      <button style={s.btnSmallRed} onClick={() => removeCapSrc(idx, csIdx)}>✕</button>
                      {cs.bank_feed_transaction_id && (() => {
                        const txn = bankFeedTxns.find(t => t.mercury_txn_id === cs.bank_feed_transaction_id);
                        if (!txn) return null;
                        const txnAmt = Math.abs(parseFloat(txn.amount || 0));
                        const allocated = getTxnAllocated(cs.bank_feed_transaction_id);
                        const thisAmt = parseFloat(cs.amount || 0);
                        return allocated > 0 || thisAmt < txnAmt ? (
                          <div data-id="split-info" style={{ fontSize: 10, color: '#64748b', marginLeft: 4, whiteSpace: 'nowrap' }}>
                            Wire: ${txnAmt.toLocaleString()} | Prior: ${allocated.toLocaleString()} | This: ${thisAmt.toLocaleString()}
                          </div>
                        ) : null;
                      })()}
                      {/* Soft envelope warning — distribution-reinvested only, recycled is informational */}
                      {(cs.source_type === 'distribution' || cs.source_type === 'recycled') && cs.source_deal && (() => {
                        const env = getSourceDealEnvelope(row.lp_id, cs.source_deal, idx, csIdx);
                        if (!env) return null;
                        const thisAmt = parseFloat(cs.amount || 0);
                        const totalReinvested = env.reinvested + thisAmt;
                        if (cs.source_type === 'distribution') {
                          if (env.distributed === 0) {
                            return (
                              <div style={{ flexBasis: '100%', fontSize: 10, color: '#b45309', marginLeft: 4 }}>
                                ⚠ No DealDistribution recorded yet for this LP from the source deal — verify the distribution exists before reinvesting.
                              </div>
                            );
                          }
                          if (totalReinvested > env.distributed * 1.01) {
                            return (
                              <div style={{ flexBasis: '100%', fontSize: 10, color: '#b45309', marginLeft: 4 }}>
                                ⚠ Reinvested ${totalReinvested.toLocaleString()} exceeds ${env.distributed.toLocaleString()} actually distributed to this LP from the source deal.
                              </div>
                            );
                          }
                          return (
                            <div style={{ flexBasis: '100%', fontSize: 10, color: '#64748b', marginLeft: 4 }}>
                              Distributed from source deal to this LP: ${env.distributed.toLocaleString()} | Reinvested (incl. this): ${totalReinvested.toLocaleString()}
                            </div>
                          );
                        }
                        // recycled — info only, no enforcement
                        return (
                          <div style={{ flexBasis: '100%', fontSize: 10, color: '#64748b', marginLeft: 4 }}>
                            Recycled from source deal — envelope check pending waterfall integration. (Distributed so far to this LP: ${env.distributed.toLocaleString()})
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                  <button style={{ ...s.btnSmall, marginTop: 4 }} onClick={() => addCapSrc(idx)}>+ Add Source</button>
                  {errs[`inv_${row.lp_id}_capsrc`] && (
                    <div style={{ ...s.fieldError, marginTop: 6 }}>{errs[`inv_${row.lp_id}_capsrc`]}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
          {renderPoolConfig()}
    </div>
  );

  /* ── Step 3: Review ── */
  const renderStep3 = () => (
    <div style={s.card}>
      <div style={s.cardTitle}>Review & Submit</div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 8 }}>Deal</div>
        <div style={s.reviewRow}><span style={s.reviewLabel}>Name</span><span style={s.reviewValue}>{deal.deal_name}</span></div>
        <div style={s.reviewRow}><span style={s.reviewLabel}>Counterparty</span><span style={s.reviewValue}>{deal.counterparty}</span></div>
        <div style={s.reviewRow}><span style={s.reviewLabel}>Deal Size</span><span style={s.reviewValue}>{fmtUSD(deal.deal_size)}</span></div>
        {deal.face_value && <div style={s.reviewRow}><span style={s.reviewLabel}>Face / Net / OID</span><span style={s.reviewValue}>{fmtUSD(deal.face_value)} / {fmtUSD(deal.net_amount)} / {deal.oid_pct || '0'}%</span></div>}
        {deal.funding_date && <div style={s.reviewRow}><span style={s.reviewLabel}>Funding Date</span><span style={s.reviewValue}>{deal.funding_date}</span></div>}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.purple, marginBottom: 8 }}>
          Security — {INSTRUMENT_TYPES.find(t => t.value === secType)?.label}
        </div>
        <div style={s.reviewRow}><span style={s.reviewLabel}>Primary</span><span style={s.reviewValue}>{sec.ticker} — {sec.name}</span></div>
        {isConvertible(secType) && (<>
          <div style={s.reviewRow}>
            <span style={s.reviewLabel}>Conversion</span>
            <span style={s.reviewValue}>
              {conversionMode === 'fixed'
                ? `Fixed: $${sec.conversion_price || '—'} / Ratio: ${sec.conversion_ratio || '—'}`
                : `Variable: ${sec.conversion_formula || '—'} (${sec.conversion_discount_pct || '—'}% disc, ${sec.conversion_vwap_lookback || '—'}d VWAP)`}
            </span>
          </div>
          {createEquity && equity.ticker && (
            <div style={s.reviewRow}><span style={s.reviewLabel}>Equity Target</span><span style={s.reviewValue}>{equity.ticker} — {equity.name}</span></div>
          )}
        </>)}
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 8 }}>Investors ({selectedInvestors.length})</div>
        {selectedInvestors.map(inv => {
          const totalFee = inv.fee_lines.reduce((a, fl) => a + (parseFloat(fl.fee_pct) || 0), 0);
          return (
            <div key={inv.lp_id} style={{ ...s.reviewRow, flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>{inv.investor_name}</span>
                <span style={s.reviewValue}>{fmtUSD(inv.invested_amount)} ({inv.ownership_pct}%)</span>
              </div>
              {inv.fee_lines.length > 0 ? (
                <div style={{ fontSize: 12, color: C.orange, marginLeft: 16 }}>
                  {inv.fee_lines.map((fl, i) => (
                    <span key={i}>
                      {fl.fee_pct}% → {fl.recipient_type === 'DealTeam' ? 'Deal Team' : 'Mgmt Co'}
                      {i < inv.fee_lines.length - 1 ? ' + ' : ''}
                    </span>
                  ))}
                  <span style={{ color: C.muted }}> = {totalFee}% total</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: C.muted, marginLeft: 16, fontStyle: 'italic' }}>No incentive fee</div>
              )}
            </div>
          );
        })}
        {/* Deal Team Pool */}
        {poolRecipients.some(pr => pr.name.trim()) && (
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.purple, marginBottom: 6 }}>Deal Team Pool</div>
            {poolRecipients.filter(pr => pr.name.trim()).map((pr, i) => (
              <div key={i} style={{ ...s.reviewRow, paddingLeft: 16 }}>
                <span style={s.reviewLabel}>{pr.name} ({pr.type})</span>
                <span style={s.reviewValue}>{pr.pct || 0}%</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ ...s.reviewRow, borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8 }}>
          <span style={{ ...s.reviewLabel, fontWeight: 700 }}>Total</span>
          <span style={s.reviewValue}>{fmtUSD(totalAllocated)} ({totalOwnership.toFixed(2)}%)</span>
        </div>
      </div>
    </div>
  );

  /* Done */
  if (done) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.green, marginBottom: 8 }}>{isEditMode ? 'Deal Updated Successfully' : 'Deal Created Successfully'}</div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>{isEditMode ? 'All changes saved successfully.' : 'All records created. Book the position on the Positions page.'}</div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ ...s.card, padding: 12, minWidth: 100 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>DEAL</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.accent }}>{createdIds.dealId}</div>
            </div>
            <div style={{ ...s.card, padding: 12, minWidth: 100 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>SECURITY</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.purple }}>{createdIds.secId}</div>
            </div>
            {createdIds.eqId && (
              <div style={{ ...s.card, padding: 12, minWidth: 100 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>EQUITY</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>{createdIds.eqId}</div>
              </div>
            )}
            <div style={{ ...s.card, padding: 12, minWidth: 100 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>INVESTORS</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.orange }}>{createdIds.invIds?.length}</div>
            </div>
            {createdIds.poolCount > 0 && (
              <div style={{ ...s.card, padding: 12, minWidth: 100 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>POOL MEMBERS</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.purple }}>{createdIds.poolCount}</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
            {isEditMode ? (
              <button style={s.btnPrimary} onClick={() => navigate('/deals')}>← Back to Deals</button>
            ) : (
              <button style={s.btnPrimary} onClick={() => { setDone(false); setStep(0); setCreatedIds({}); }}>+ New Deal</button>
            )}
            <button style={s.btnSecondary} onClick={() => navigate('/deals')}>View All Deals</button>
          </div>
        </div>
      </div>
    );
  }

  if (editLoading) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 18, color: C.muted, marginBottom: 8 }}>Loading deal data...</div>
          <div style={{ fontSize: 13, color: C.muted }}>Fetching deal #{editDealId} with all related records</div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h1 style={s.title}>{isEditMode ? (deal.deal_name ? `Edit Deal: ${deal.deal_name}` : `Edit Deal #${editDealId}`) : 'Deal Setup Wizard'}</h1>
      <p style={s.subtitle}>{isEditMode ? 'Modify deal details, security terms, and investor allocations' : 'Create a complete deal — securities, terms, and investor allocations'}</p>
      <div style={s.stepper}>
        {STEPS.map((label, i) => (
          <div key={i} style={{ ...s.step, ...(i === step ? s.stepActive : {}), ...(i < step ? s.stepDone : {}), cursor: i < step ? 'pointer' : 'default' }}
            onClick={() => { if (i < step) { setTouched(false); setStep(i); } }}>
            <div style={s.stepNum}>{i < step ? '✓' : `STEP ${i + 1}`}</div>
            {label}
          </div>
        ))}
      </div>
      {error && <div style={s.error}>{error}</div>}
      {step === 0 && renderStep0()}
      {step === 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>Strategy & Classification</div>
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Strategy Tag</label>
              <select style={s.select} value={deal.strategy_tag} onChange={e => setDeal({...deal, strategy_tag: e.target.value})}>
                <option value="">— Select —</option>
                <option value="Convertible Note">Convertible Note</option>
                <option value="Convertible Preferred">Convertible Preferred</option>
                <option value="ELOC">ELOC</option>
                <option value="ACE Deal">ACE Deal</option>
                <option value="Registered Direct">Registered Direct (RD)</option>
                <option value="PPLN">PPLN</option>
                <option value="IPO">IPO</option>
                <option value="deSPAC - Convertible Note">deSPAC - Convertible Note</option>
                <option value="Common Equity PIPE">Common Equity PIPE</option>
                <option value="Bridge Loan">Bridge Loan</option>
                <option value="Warrant">Warrant</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Vintage Year</label>
              <input style={s.input} type="number" value={deal.vintage_year} onChange={e => setDeal({...deal, vintage_year: e.target.value})} placeholder="2025" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Coupon Frequency</label>
              <select style={s.select} value={deal.coupon_frequency} onChange={e => setDeal({...deal, coupon_frequency: e.target.value})}>
                <option value="">— Select —</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
                <option value="At Maturity">At Maturity</option>
                <option value="PIK">PIK (accrues)</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Warrant Coverage %</label>
              <PctField style={s.input} value={deal.warrant_coverage_pct} onChange={v => setDeal({...deal, warrant_coverage_pct: v})} placeholder="0" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Affiliate Status</label>
              <select style={s.select} value={deal.affiliate_status} onChange={e => setDeal({...deal, affiliate_status: e.target.value})}>
                <option value="">— Select —</option>
                <option value="Non-Affiliate">Non-Affiliate</option>
                <option value="Affiliate">Affiliate</option>
                <option value="10% Holder">10%+ Holder</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Beneficial Ownership %</label>
              <PctField style={s.input} value={deal.beneficial_ownership_pct} onChange={v => setDeal({...deal, beneficial_ownership_pct: v})} placeholder="0" />
            </div>
          </div>
        </div>
      )}
      {step === 1 && isEditMode && (
        <div style={{ ...s.card, background: '#1a1a2e', border: '1px solid #3b82f640', padding: '10px 16px', marginBottom: 0 }}>
          <span style={{ fontSize: 12, color: C.accent }}>ℹ️ Editing security terms for the linked security. Changing instrument type may require manual data cleanup.</span>
        </div>
      )}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      <div style={s.btnRow}>
        <button style={step === 0 ? { ...s.btnSecondary, ...s.btnDisabled } : s.btnSecondary}
          onClick={goBack} disabled={step === 0}>← Back</button>
        {step < 3 ? (
          <button style={s.btnPrimary} onClick={tryNext}>Next &rarr;</button>
        ) : (
          <button style={{ ...s.btnPrimary, background: "#2d6a4f", ...(submitting ? s.btnDisabled : {}) }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEditMode ? '✓ Save Changes' : '✓ Create Deal'}
          </button>
        )}
      </div>
    </div>
  );
}
